/**
 * Editing-string ⇄ TemplateValue conversion for the FieldInput component.
 *
 * The overlay-highlight input keeps a real <textarea>/<input> whose text is a
 * flat editing string. Field references are written in that string as
 * `/FieldName` (optionally `/FieldName|upper|default:"--"`) and painted as a
 * pill by the overlay. `{{ }}` never appears. Workbook (processed) fields use a
 * `/out:FieldName` form so they survive a round-trip even when the live field
 * list doesn't include them yet.
 *
 * Parsing is field-list aware: after a `/`, the LONGEST known field name that
 * matches is consumed, so multi-word headers ("Variant Price") tokenise
 * unambiguously. A `/` that matches no field stays literal text — the input
 * degrades gracefully and never throws.
 *
 * Pure + framework-free so it is unit-testable without React.
 */

import {
  toTemplateValue,
  type Pipe,
  type Segment,
  type SegmentOrigin,
  type TemplateValue,
} from "./templateValue";

const WORKBOOK_PREFIX = "out:";

/** A field the input knows about, used to disambiguate `/multi word` tokens. */
export type KnownField = { name: string; origin: SegmentOrigin };

// ---------------------------------------------------------------------------
// segments → editing string
// ---------------------------------------------------------------------------

function pipeToText(p: Pipe): string {
  if (!p.args || p.args.length === 0) return `|${p.name}`;
  const args = p.args.map((a) => `"${a.replace(/"/g, '\\"')}"`).join(",");
  return `|${p.name}:${args}`;
}

function fieldToText(seg: Extract<Segment, { t: "field" }>): string {
  const prefix = seg.origin === "workbook" ? WORKBOOK_PREFIX : "";
  const pipes = (seg.pipes ?? []).map(pipeToText).join("");
  return `/${prefix}${seg.name}${pipes}`;
}

/** Render a TemplateValue (or legacy string) as the flat editing string. */
export function segmentsToEditing(
  value: string | TemplateValue | null | undefined,
): string {
  const segments = toTemplateValue(value);
  let out = "";
  for (const seg of segments) {
    out += seg.t === "text" ? seg.v : fieldToText(seg);
  }
  return out;
}

// ---------------------------------------------------------------------------
// editing string → segments
// ---------------------------------------------------------------------------

/** Characters that end a field token's name/pipes run when typing freely. */
function isPipeBoundary(ch: string): boolean {
  return ch === "/" || ch === "\n" || ch === "\r";
}

/** Parse `name:"a","b"` style pipe args after a `|`. Mirrors the engine. */
function parsePipe(expr: string): Pipe {
  const colon = expr.indexOf(":");
  if (colon === -1) return { name: expr.trim() };
  const name = expr.slice(0, colon).trim();
  const argStr = expr.slice(colon + 1).trim();
  const args: string[] = [];
  let i = 0;
  while (i < argStr.length) {
    if (argStr[i] === "," || argStr[i] === " ") { i++; continue; }
    if (argStr[i] === '"' || argStr[i] === "'") {
      const q = argStr[i];
      i++;
      let a = "";
      while (i < argStr.length && argStr[i] !== q) { a += argStr[i]; i++; }
      i++;
      args.push(a);
    } else {
      let a = "";
      while (i < argStr.length && argStr[i] !== ",") { a += argStr[i]; i++; }
      args.push(a.trim());
    }
  }
  return args.length ? { name, args } : { name };
}

/**
 * Match a field token starting just after a `/` at `text[start]`. Returns the
 * matched field name, its origin, and the index after the name — or null when
 * no known field matches (so the `/` is treated as literal text).
 */
function matchFieldName(
  text: string,
  start: number,
  known: KnownField[],
): { name: string; origin: SegmentOrigin; end: number } | null {
  let body = text.slice(start);
  let origin: SegmentOrigin = "source";
  let offset = start;
  if (body.toLowerCase().startsWith(WORKBOOK_PREFIX)) {
    origin = "workbook";
    offset += WORKBOOK_PREFIX.length;
    body = text.slice(offset);
  }
  // Longest known name that the text begins with (case-insensitive).
  const candidates = known
    .filter((f) => (origin === "workbook" ? f.origin === "workbook" : true))
    .map((f) => f.name)
    .filter((n) => n && body.toLowerCase().startsWith(n.toLowerCase()))
    .sort((a, b) => b.length - a.length);
  if (candidates.length > 0) {
    const name = candidates[0];
    return { name, origin, end: offset + name.length };
  }
  return null;
}

/**
 * Parse the flat editing string into a TemplateValue, using `known` fields to
 * resolve `/multi word` tokens. Adjacent text is coalesced; unknown `/x` stays
 * literal.
 */
export function editingToSegments(text: string, known: KnownField[]): TemplateValue {
  const out: TemplateValue = [];
  let buf = "";
  let i = 0;
  const flush = () => {
    if (buf) { out.push({ t: "text", v: buf }); buf = ""; }
  };
  while (i < text.length) {
    if (text[i] === "/") {
      const match = matchFieldName(text, i + 1, known);
      if (match) {
        flush();
        // Collect trailing `|pipe` runs.
        let j = match.end;
        const pipes: Pipe[] = [];
        while (text[j] === "|") {
          let k = j + 1;
          while (k < text.length && !isPipeBoundary(text[k]) && text[k] !== "|") k++;
          const expr = text.slice(j + 1, k).trim();
          if (expr) pipes.push(parsePipe(expr));
          j = k;
        }
        const seg: Segment = { t: "field", name: match.name, origin: match.origin };
        if (pipes.length) seg.pipes = pipes;
        out.push(seg);
        i = j;
        continue;
      }
    }
    buf += text[i];
    i++;
  }
  flush();
  return out;
}

// ---------------------------------------------------------------------------
// Overlay painting — segment character ranges within the editing string
// ---------------------------------------------------------------------------

export type PaintToken =
  | { kind: "text"; text: string }
  | { kind: "field"; text: string; origin: SegmentOrigin; label: string };

/** Break the editing string into paint tokens (text runs + field pills). */
export function paintTokens(text: string, known: KnownField[]): PaintToken[] {
  const segments = editingToSegments(text, known);
  const tokens: PaintToken[] = [];
  for (const seg of segments) {
    if (seg.t === "text") {
      tokens.push({ kind: "text", text: seg.v });
    } else {
      tokens.push({
        kind: "field",
        text: fieldToText(seg),
        origin: seg.origin,
        label: seg.name,
      });
    }
  }
  return tokens;
}
